import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, CircleCheck, Clock, CloudOff, Eye, History, RotateCcw, X } from 'lucide-react';
import { LecteurVideo, type CommandesLecteur } from './LecteurVideo';
import { DiscussionModule } from './DiscussionModule';
import { DevoirsDuModule } from './DevoirsDuModule';
import { NotesLecon } from './NotesLecon';
import { RessourcesLecon } from './RessourcesLecon';
import { TranscriptionLecon } from './TranscriptionLecon';
import { BarreAvancement, IconeLecon, leconsTerminees, tauxLisible } from './Progression';
import { Chargement, MessageErreur, Pastille } from '../ui/Primitives';
import { ErreurApi } from '../../lib/api';
import { dureeLisible } from '../../lib/useElearning';
import { tempsLisible } from '../../lib/temps-video';
import { useEnregistrementVisionnage } from '../../lib/useVisionnage';
import {
  adresseLecon,
  nombreDeRessources,
  urlSousTitresLecon,
  urlVideoLecon,
  useLeconLecteur,
  type VueLecon,
} from '../../lib/useLecteur';
import { clavierOnglets } from '../ui/onglets-aria';

type CleOnglet = 'apercu' | 'notes' | 'ressources' | 'transcription' | 'discussion';

const ONGLETS: { cle: CleOnglet; libelle: string }[] = [
  { cle: 'apercu', libelle: "Vue d'ensemble" },
  { cle: 'notes', libelle: 'Mes notes' },
  { cle: 'ressources', libelle: 'Ressources' },
  { cle: 'transcription', libelle: 'Transcription' },
  { cle: 'discussion', libelle: 'Discussion' },
];

/**
 * Le programme du cours à côté de la leçon. L'apprenant y voit ses leçons
 * terminées, le taux de chaque module et celui du cours (TF-451) ; l'aperçu,
 * où rien n'est suivi, le nombre de leçons seulement. Chaque module liste ses
 * devoirs et quiz après ses leçons, la fin du cours les siens (TF-454).
 */
function Programme({ vue }: { vue: VueLecon }): JSX.Element {
  const navigate = useNavigate();
  const lecons = vue.programme.flatMap((module) => module.lecons);
  return (
    <aside className="scrollbar-thin overflow-y-auto border-gray-200 bg-white lg:border-l" aria-label="Programme du cours">
      <div className="border-b border-gray-100 p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-display font-bold text-codialis-navy">Programme</h3>
          <span className={`shrink-0 text-xs ${vue.avancement ? 'font-bold text-codialis-mint-clair' : 'text-codialis-gray'}`}>
            {vue.avancement
              ? vue.avancement.taux !== null && `${tauxLisible(vue.avancement)} complété`
              : `${lecons.length} leçon${lecons.length > 1 ? 's' : ''}`}
          </span>
        </div>
        <p className="mt-1 truncate text-xs text-codialis-gray">{vue.cours.titre}</p>
        {vue.avancement && (
          <>
            <div className="mt-2">
              <BarreAvancement avancement={vue.avancement} libelle="Progression du cours" />
            </div>
            <p className="mt-1.5 text-xs text-codialis-gray">
              {vue.avancement.taux === null ? 'Aucune leçon à suivre pour l’instant' : leconsTerminees(vue.avancement)}
            </p>
          </>
        )}
      </div>
      <ol>
        {vue.programme.map((module, rangModule) => (
          <li key={module.id} className="border-b border-gray-100 px-3 py-3 sm:px-4">
            <div className="flex items-baseline justify-between gap-2 px-1">
              <span className="min-w-0 text-xs font-semibold uppercase tracking-wide text-codialis-gray">
                Module {rangModule + 1} · {module.titre}
              </span>
              {module.avancement && module.avancement.taux !== null && (
                <span
                  className={`shrink-0 text-xs font-bold ${module.avancement.taux === 100 ? 'text-codialis-mint-clair' : 'text-codialis-gray'}`}
                >
                  {tauxLisible(module.avancement)}
                </span>
              )}
            </div>
            <ol className="mt-1 space-y-1">
              {module.lecons.map((lecon, rangLecon) => {
                const courante = lecon.id === vue.lecon.id;
                return (
                  <li key={lecon.id}>
                    <button
                      type="button"
                      onClick={() => navigate(adresseLecon(lecon.id))}
                      aria-current={courante ? 'true' : undefined}
                      className={`flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2 text-left text-sm transition ${
                        courante ? 'bg-codialis-mint/15 font-semibold text-codialis-navy' : 'text-codialis-navy hover:bg-gray-50'
                      }`}
                    >
                      <IconeLecon lecon={lecon} />
                      <span className="w-7 shrink-0 font-mono text-[11px] text-codialis-gray">
                        {rangModule + 1}.{rangLecon + 1}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{lecon.titre}</span>
                      <span className="shrink-0 text-[11px] text-codialis-gray">
                        {lecon.video
                          ? (dureeLisible(lecon.dureeSecondes) ?? '')
                          : lecon.ressources > 0
                            ? nombreDeRessources(lecon.ressources)
                            : 'À venir'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <DevoirsDuModule devoirs={module.devoirs} />
          </li>
        ))}
        {vue.evaluations.length > 0 && (
          <li className="border-b border-gray-100 px-3 py-3 sm:px-4">
            <span className="px-1 text-xs font-semibold uppercase tracking-wide text-codialis-gray">Évaluations du cours</span>
            <DevoirsDuModule devoirs={vue.evaluations} />
          </li>
        )}
      </ol>
    </aside>
  );
}

/**
 * Une leçon dans le lecteur (TF-444) : sa vidéo — vitesse, sous-titres, plein
 * écran —, le programme du cours, les leçons précédente et suivante. L'auteur
 * et la direction la voient en aperçu, comme l'apprenant la verra. Sous la
 * vidéo, sa transcription (TF-445) et les notes de l'apprenant (TF-446)
 * suivent la lecture et y renvoient ; ses ressources annexes (TF-447) s'y
 * consultent et s'y téléchargent ; la discussion de son module (TF-448) s'y
 * lit et s'y écrit. `?onglet=transcription|notes|ressources|discussion`
 * ouvre l'onglet d'emblée, `?t=` fait commencer la vidéo à ce moment (une
 * note ouverte depuis « Mes notes »), `?question=` fait défiler la discussion
 * jusqu'à une question (ouverte depuis une notification).
 *
 * La vidéo de l'apprenant reprend là où il s'est arrêté, sur n'importe quel
 * appareil (TF-449) : sa position part pendant la lecture, et la leçon rouverte
 * la reprend, avec de quoi recommencer au début. Un `?t=` l'emporte.
 */
export function LeconEnLecture({ leconId }: { leconId: string }): JSX.Element {
  const navigate = useNavigate();
  const [parametres] = useSearchParams();
  const [onglet, setOnglet] = useState<CleOnglet>(() => {
    const demande = parametres.get('onglet');
    return demande === 'transcription' || demande === 'notes' || demande === 'ressources' || demande === 'discussion'
      ? demande
      : 'apercu';
  });
  const questionCiblee = parametres.get('question');
  const [depart] = useState(() => {
    const valeur = Number(parametres.get('t'));
    return Number.isFinite(valeur) && valeur > 0 ? valeur : null;
  });
  const commandes = useRef<CommandesLecteur>(null);
  const [temps, setTemps] = useState(0);
  // Le temps n'est rendu qu'aux onglets qui le lisent ; à leur ouverture, ils
  // repartent du dernier lu, même vidéo en pause.
  const dernierTemps = useRef(0);
  const choisirOnglet = (cle: CleOnglet) => {
    if (cle === 'transcription' || cle === 'notes') setTemps(dernierTemps.current);
    setOnglet(cle);
  };
  const client = useQueryClient();
  const { data: vue, isLoading, error } = useLeconLecteur(leconId);
  // La vidéo vue jusqu'au bout termine la leçon (TF-451) : le programme, les
  // cours et l'espace apprenant se relisent. La reprise, déjà lue, ne bouge pas.
  const suivi = useEnregistrementVisionnage(leconId, vue?.lecon.visionnage ? (vue.lecon.video?.id ?? null) : null, () => {
    void client.invalidateQueries({ queryKey: ['lecteur', 'lecon', leconId], exact: true });
    void client.invalidateQueries({ queryKey: ['lecteur', 'cours'] });
    void client.invalidateQueries({ queryKey: ['espace-apprenant'] });
  });
  // La reprise est lue à l'ouverture de la leçon, puis ne bouge plus.
  const [reprise, setReprise] = useState<number | null | undefined>(undefined);
  const [bandeauReprise, setBandeauReprise] = useState(true);
  if (vue && reprise === undefined) setReprise(depart === null ? (vue.lecon.visionnage?.repriseSecondes ?? null) : null);

  if (isLoading) return <Chargement libelle="Chargement de la leçon…" />;
  if (error || !vue) {
    return (
      <div className="space-y-3 p-4 sm:p-6">
        <MessageErreur message={error instanceof ErreurApi ? error.message : 'La leçon n’a pas pu être chargée.'} />
        <button
          type="button"
          onClick={() => navigate('/player')}
          className="text-sm font-semibold text-codialis-sky hover:underline"
        >
          Retour au lecteur
        </button>
      </div>
    );
  }

  const apercu = vue.acces === 'APERCU';
  const retour = () => navigate(apercu ? `/instructor-create?cours=${encodeURIComponent(vue.cours.id)}&etape=3` : '/player');
  const { lecon } = vue;
  // Dernière leçon d'un module : ses devoirs et quiz encore à faire, et ceux de
  // la fin du cours après sa dernière leçon (maquette : « Module terminé » mène au quiz).
  const moduleCourant = vue.programme.find((module) => module.id === lecon.module.id);
  const derniereDuModule = moduleCourant?.lecons[moduleCourant.lecons.length - 1]?.id === lecon.id;
  const aFaire = [
    ...(derniereDuModule ? (moduleCourant?.devoirs ?? []) : []),
    ...(vue.suivante ? [] : vue.evaluations),
  ].filter((devoir) => devoir.statut === 'A_RENDRE');

  return (
    <div className="min-h-screen bg-codialis-ink">
      <div className="sticky top-0 z-20 border-b border-gray-200 bg-white">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={retour}
            className="flex min-h-[40px] items-center gap-2 text-sm font-medium text-codialis-navy hover:text-codialis-sky"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            {apercu ? 'Retour au cours' : 'Retour'}
          </button>
          <div className="hidden h-6 w-px bg-gray-200 sm:block" />
          <div className="min-w-0 flex-1 basis-48">
            <div className="truncate text-xs text-codialis-gray">
              {vue.cours.titre} · Module {lecon.numero.split('.')[0]} · {lecon.module.titre}
            </div>
            <h2 className="font-display truncate font-bold text-codialis-navy">
              {lecon.numero} · {lecon.titre}
            </h2>
          </div>
          {lecon.visionnage && lecon.video && (
            <span
              title="Votre position dans la vidéo est sauvegardée automatiquement : elle reprendra ici, sur tous vos appareils."
              className="hidden items-center gap-2 text-xs text-codialis-gray md:flex"
            >
              {suivi.etat === 'echec' ? (
                <>
                  <CloudOff className="h-4 w-4 text-amber-600" aria-hidden />
                  Position non sauvegardée
                </>
              ) : (
                <>
                  <CircleCheck className="h-4 w-4 text-codialis-mint" aria-hidden />
                  Position sauvegardée
                </>
              )}
            </span>
          )}
          {apercu && (
            <span className="inline-flex items-center gap-1.5 text-xs text-codialis-gray">
              <Eye className="h-4 w-4" aria-hidden />
              Aperçu
              <Pastille texte={vue.cours.statutLibelle} ton={vue.cours.statut === 'PUBLIE' ? 'success' : 'warning'} />
            </span>
          )}
          {vue.suivante && (
            <button
              type="button"
              onClick={() => navigate(adresseLecon(vue.suivante!.id))}
              className="flex min-h-[40px] items-center gap-2 rounded-lg bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep"
            >
              Leçon suivante
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </div>

      <div className="grid min-h-[calc(100vh-110px)] grid-cols-1 lg:grid-cols-4">
        <div className="min-w-0 bg-codialis-ink lg:col-span-3">
          {lecon.video ? (
            <LecteurVideo
              key={lecon.video.id}
              ref={commandes}
              onTemps={(secondes, duree) => {
                suivi.surTemps(secondes, duree);
                dernierTemps.current = secondes;
                if (onglet === 'transcription' || onglet === 'notes') setTemps(secondes);
              }}
              onEtat={suivi.surEtat}
              depart={depart ?? reprise ?? null}
              source={urlVideoLecon(lecon.id, lecon.video.id)}
              titre={lecon.titre}
              dureeConnue={lecon.video.dureeSecondes}
              autoPlay
              sousTitres={
                lecon.sousTitres
                  ? { source: urlSousTitresLecon(lecon.id, lecon.sousTitres.version), langue: 'fr', libelle: 'Français' }
                  : null
              }
            />
          ) : (
            <div className="video-area flex aspect-video items-center justify-center p-4 text-center sm:p-6">
              <div className="max-w-md text-white/80">
                <Clock className="mx-auto mb-3 h-10 w-10 text-white/30" aria-hidden />
                <p className="text-sm leading-relaxed">La vidéo de cette leçon n’est pas encore disponible.</p>
              </div>
            </div>
          )}

          <div className="bg-white">
            {lecon.video && reprise != null && bandeauReprise && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-gray-200 bg-codialis-sky/10 px-4 py-2 text-sm text-codialis-navy sm:px-6">
                <History className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
                {/* `basis-56` : sur téléphone, le bouton passe sous le texte plutôt que de l'écraser. */}
                <span className="min-w-0 flex-1 basis-56">
                  Reprise à <strong>{tempsLisible(reprise)}</strong>, là où vous vous êtes arrêté(e).
                </span>
                <button
                  type="button"
                  onClick={() => {
                    commandes.current?.allerA(0);
                    setBandeauReprise(false);
                  }}
                  className="inline-flex min-h-[36px] items-center gap-1.5 font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
                >
                  <RotateCcw className="h-4 w-4" aria-hidden />
                  Reprendre au début
                </button>
                <button
                  type="button"
                  onClick={() => setBandeauReprise(false)}
                  aria-label="Fermer"
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-codialis-gray transition hover:bg-white"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
            )}
            <div
              role="tablist"
              aria-label="Contenu de la leçon"
              onKeyDown={clavierOnglets(
                ONGLETS.map((entree) => entree.cle),
                onglet,
                choisirOnglet,
              )}
              className="relative flex gap-6 overflow-x-auto border-b border-gray-200 px-4 sm:px-6"
            >
              {ONGLETS.map((entree) => (
                <button
                  key={entree.cle}
                  type="button"
                  role="tab"
                  id={`onglet-lecon-${entree.cle}`}
                  aria-selected={onglet === entree.cle}
                  aria-controls={`panneau-lecon-${entree.cle}`}
                  tabIndex={onglet === entree.cle ? 0 : -1}
                  data-onglet={entree.cle}
                  onClick={() => choisirOnglet(entree.cle)}
                  // `shrink-0` : au doigt, la largeur minimale des boutons
                  // (index.css) laisserait les onglets se tasser l'un sur l'autre.
                  className={`tab-btn shrink-0 whitespace-nowrap border-b-2 py-4 text-sm ${onglet === entree.cle ? 'active' : ''}`}
                >
                  {entree.libelle}
                  {entree.cle === 'ressources' && lecon.ressources.length > 0 && (
                    <>
                      {' '}
                      <span className="badge-info rounded px-1.5 py-0.5 text-[10px]">{lecon.ressources.length}</span>
                    </>
                  )}
                  {entree.cle === 'discussion' && vue.discussion.questions > 0 && (
                    <>
                      {' '}
                      <span className="badge-warning rounded px-1.5 py-0.5 text-[10px]">{vue.discussion.questions}</span>
                    </>
                  )}
                </button>
              ))}
            </div>

            <div className="p-4 sm:p-6">
              {onglet === 'apercu' ? (
                <div role="tabpanel" id="panneau-lecon-apercu" aria-labelledby="onglet-lecon-apercu">
                  <h3 className="font-display mb-1 text-xl font-bold text-codialis-navy">{lecon.titre}</h3>
                  <p className="mb-4 text-sm text-codialis-gray">
                    Module {lecon.numero.split('.')[0]} · {lecon.module.titre} · par {vue.cours.auteur}
                  </p>
                  <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    {(
                      [
                        ['Durée', lecon.video ? (dureeLisible(lecon.video.dureeSecondes) ?? '—') : 'Vidéo à venir'],
                        ['Sous-titres', lecon.sousTitres ? 'Français' : 'Aucun'],
                        ['Ressources', lecon.ressources.length > 0 ? nombreDeRessources(lecon.ressources.length) : 'Aucune'],
                        ['Titre certifiant', vue.cours.formation],
                      ] as const
                    ).map(([libelle, valeur]) => (
                      <div key={libelle} className="min-w-0 rounded-xl bg-gray-50 p-3">
                        <div className="text-xs uppercase text-codialis-gray">{libelle}</div>
                        <div className="font-display truncate font-bold text-codialis-navy" title={valeur}>
                          {valeur}
                        </div>
                      </div>
                    ))}
                  </div>
                  {vue.cours.description && (
                    <>
                      <h4 className="font-display mb-2 font-bold text-codialis-navy">À propos de ce cours</h4>
                      <p className="mb-4 whitespace-pre-line text-sm leading-relaxed text-codialis-gray">
                        {vue.cours.description}
                      </p>
                    </>
                  )}
                  {aFaire.length > 0 && (
                    <div className="mb-4 rounded-xl border border-codialis-mint/40 bg-codialis-mint/10 p-3 sm:p-4">
                      <div className="px-2 text-sm font-semibold text-codialis-navy">
                        {vue.suivante ? 'Pour clore ce module' : 'Pour clore ce cours'}
                      </div>
                      <DevoirsDuModule devoirs={aFaire} taille="large" />
                    </div>
                  )}
                  <div className="flex flex-wrap justify-between gap-2">
                    {vue.precedente ? (
                      <button
                        type="button"
                        onClick={() => navigate(adresseLecon(vue.precedente!.id))}
                        className="inline-flex min-h-[44px] min-w-0 max-w-full items-center gap-2 rounded-xl border border-gray-200 px-4 text-sm font-medium text-codialis-navy hover:bg-gray-50"
                      >
                        <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
                        <span className="truncate">{vue.precedente.titre}</span>
                      </button>
                    ) : (
                      <span />
                    )}
                    {vue.suivante && (
                      <button
                        type="button"
                        onClick={() => navigate(adresseLecon(vue.suivante!.id))}
                        className="inline-flex min-h-[44px] min-w-0 max-w-full items-center gap-2 rounded-xl border border-gray-200 px-4 text-sm font-medium text-codialis-navy hover:bg-gray-50"
                      >
                        <span className="truncate">{vue.suivante.titre}</span>
                        <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                      </button>
                    )}
                  </div>
                </div>
              ) : onglet === 'notes' ? (
                <div role="tabpanel" id="panneau-lecon-notes" aria-labelledby="onglet-lecon-notes">
                  <NotesLecon
                    vue={vue}
                    temps={temps}
                    allerA={lecon.video ? (secondes) => commandes.current?.allerA(secondes) : null}
                  />
                </div>
              ) : onglet === 'ressources' ? (
                <div role="tabpanel" id="panneau-lecon-ressources" aria-labelledby="onglet-lecon-ressources">
                  <RessourcesLecon vue={vue} />
                </div>
              ) : onglet === 'transcription' ? (
                <div role="tabpanel" id="panneau-lecon-transcription" aria-labelledby="onglet-lecon-transcription">
                  <h3 className="font-display mb-3 text-xl font-bold text-codialis-navy">Transcription automatique</h3>
                  <TranscriptionLecon
                    vue={vue}
                    temps={temps}
                    allerA={lecon.video ? (secondes) => commandes.current?.allerA(secondes) : null}
                  />
                </div>
              ) : (
                <div role="tabpanel" id="panneau-lecon-discussion" aria-labelledby="onglet-lecon-discussion">
                  <DiscussionModule key={lecon.module.id} vue={vue} questionCiblee={questionCiblee} />
                </div>
              )}
            </div>
          </div>
        </div>

        <Programme vue={vue} />
      </div>
    </div>
  );
}
